import upperFirst from 'lodash/upperFirst'

import {
  Policy as FetchedPolicy,
  PolicyInput,
  PolicyInput_KindEnum,
  Policy_KindEnum,
  PolicyRule as FetchedPolicyRule,
  PolicyRule_RuleUnitsEnum,
  RuleInput,
  RuleInput_RuleTypeEnum,
  Policy_PenaltyFrequencyEnum as PenaltyFrequency,
  PolicyInput_PenaltyFrequencyStrEnum,
  PolicyInput_FeeCalculationMethodEnum,
  PolicyTimeslot,
  RuleInput_RuleUnitsEnum,
  PolicyInput_DueDateKindEnum,
  PolicyInput_RecurrenceKindEnum,
  RuleInput_ProrationKindEnum,
  AgencySettings_VehicleStatesEnum,
  PolicyInput_MaintenanceKindsEnum,
  Policy_MaintenanceKindsEnum,
} from 'src/api/gen'
import {
  IBasePolicy,
  IBaseRule,
  IPolicyAbandoned,
  IPolicyAbsoluteFleetSize,
  IPolicyBalancing,
  IPolicyGeofence,
  IPolicyParking,
  IPolicyPermit,
  IPolicySidewalk,
  IPolicySpeed,
  IPolicyTime,
  IPolicyTripTaxation,
  IPolicyUser,
  IPolicyRuleSpeed,
  IPolicyRuleTripTaxation,
  Policy,
  PolicyRule,
  IPolicyBeginnerSpeedLimit,
  IPolicyRuleBeginnerSpeedLimit,
  IPolicyBeginnerAllowedHours,
  IPolicyVehicleDeployment,
  IPolicyMaintenance,
  IPolicyFleetBudget,
  IPolicyDwellTime,
} from 'src/api/types/policy'
import { getTranslation } from 'src/commons/i18n'
import { enumsTranslationKeys } from 'src/commons/i18nEnums'
import { assertUnreachable } from 'src/commons/typeHelpers'

import { stringToZonedDateTime, zonedDateTimeToISOString } from '../temporalHelpers'

const SECOND = 1000
const MINUTE = 60 * SECOND
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

/**
 * Function to format the penalty to the title.
 * @param penaltyFrequency
 */
export function getPenaltyFrequencyTitle(penaltyFrequency: PenaltyFrequency | undefined) {
  const { t } = getTranslation()
  const frequency = penaltyFrequency || PenaltyFrequency.daily
  return upperFirst(t(enumsTranslationKeys.PenaltyFrequency[frequency]))
}

/**
 * Parse an isoduration (using the format agreed with the backend: trailing T allowed)
 * and returns the Temporal.Duration
 * @param value
 */
export const parseISODuration = (value: string): Temporal.Duration => {
  return Temporal.Duration.from(value)
}

/**
 * Function to format a duration in milliseconds to isoformat, max-precision: seconde.
 * Examples:
 *    - 60000 => 'PT1S'
 *    - 6000 => 'PT0H' (by default if duration smaller than a seconde)
 * @param milliseconds
 */
export const formatDurationToISO = (milliseconds: number): string => {
  let isoduration = 'P'
  const days = Math.floor(milliseconds / DAY)
  const hours = Math.floor((milliseconds - days * DAY) / HOUR)
  const minutes = Math.floor((milliseconds - days * DAY - hours * HOUR) / MINUTE)
  const seconds = Math.floor((milliseconds - days * DAY - hours * HOUR - minutes * MINUTE) / SECOND)

  isoduration += days > 0 ? `${days}DT` : 'T'
  isoduration += hours > 0 ? `${hours}H` : ''
  isoduration += minutes > 0 ? `${minutes}M` : ''
  isoduration += seconds > 0 ? `${seconds}S` : ''
  isoduration += milliseconds === 0 ? `0H` : ''

  return isoduration
}

export function getGracePeriodTitle(kind: Policy_KindEnum) {
  const { t } = getTranslation()
  let title = t('policy.grace_period_days')
  switch (kind) {
    case Policy_KindEnum.abandoned:
      title = t('policy.grace_period_hours')
      break
    case Policy_KindEnum.geofence:
    case Policy_KindEnum.parking:
    case Policy_KindEnum.absolute_fleet_size:
      title = t('policy.grace_period_minutes')
      break
    case Policy_KindEnum.dwell_time:
      title = t('titles.grace_period.dwell_time')
      break
  }
  return title
}

export function getPolicyDelayFactor(kind: Policy_KindEnum) {
  switch (kind) {
    case Policy_KindEnum.abandoned:
    case Policy_KindEnum.dwell_time:
      return 3600
    case Policy_KindEnum.geofence:
    case Policy_KindEnum.parking:
    case Policy_KindEnum.speed:
    case Policy_KindEnum.absolute_fleet_size:
      return 60
    default:
      return 86400
  }
}

export function initialisePolicyConfig(kind: Policy_KindEnum): Policy['config'] {
  switch (kind) {
    case Policy_KindEnum.balancing:
      return {
        minimum_fleet_percentage: 0,
      }
  }
  return {}
}

/**
 * Convert the common part of the rule retrieved from the backend to the
 * format used by the front
 * @param rule
 */
const getBaseRule = (rule: FetchedPolicyRule): IBaseRule => ({
  name: rule.name,
  rule_id: rule.rule_id,
  geographies: rule.geographies,
  buffered_geographies: rule.buffered_geographies,
  vehicle_types: rule.vehicle_types,
  timeslots: rule.timeslots,
  proration_kind: rule.proration_kind,
  proration_areas_mapping: rule.proration_areas_mapping,
  proration_time_spent_report_area: rule.proration_time_spent_report_area,
  rule_type: rule.rule_type as unknown as Policy_KindEnum,
})

/**
 * Convert a policy from the backend to the format in the front.
 * Basically the front is stricter
 * @param fetchedPolicy
 */
export function getCurrentPolicy(fetchedPolicy: FetchedPolicy): Policy {
  const basePolicy: IBasePolicy = {
    ...fetchedPolicy,
    start_date: stringToZonedDateTime(fetchedPolicy.start_date.toString()),
    end_date: fetchedPolicy.end_date ? stringToZonedDateTime(fetchedPolicy.end_date.toString()) : undefined,
    published_date: fetchedPolicy.published_date
      ? stringToZonedDateTime(fetchedPolicy.published_date.toString())
      : undefined,
    confirmation_delay: (fetchedPolicy.confirmation_delay ?? 0) / getPolicyDelayFactor(fetchedPolicy.kind), // Seconds to days
    penalty_delay: (fetchedPolicy.penalty_delay ?? 0) / getPolicyDelayFactor(fetchedPolicy.kind), // Seconds to days
    notification_delay: undefined,
    smart_patrol: fetchedPolicy.smart_patrol ?? false,
    is_mds: fetchedPolicy.is_mds ?? false,
  }

  switch (fetchedPolicy.kind) {
    case Policy_KindEnum.speed: {
      const speedPolicy: IPolicySpeed = {
        ...basePolicy,
        kind: Policy_KindEnum.speed,
        config: {},
        minimum_time_duration: fetchedPolicy.minimum_time_duration ?? 0,
        rules: fetchedPolicy.rules.map((rule) => ({
          ...getBaseRule(rule),
          rule_units: rule.rule_units as IPolicyRuleSpeed['rule_units'],
          rule_type: Policy_KindEnum.speed,
          maximum: rule.maximum,
          buffer_speed: rule.buffer_speed,
        })),
      }
      return speedPolicy
    }
    case Policy_KindEnum.permit: {
      const permitPolicy: IPolicyPermit = {
        ...basePolicy,
        kind: Policy_KindEnum.permit,
        config: {},
        rules: fetchedPolicy.rules.map((r) => ({
          ...getBaseRule(r),
          rule_type: Policy_KindEnum.permit,
          minimum: r.minimum as number,
          maximum: r.maximum as number,
          rate_amount: r.rate_amount as number,
        })),
      }
      return permitPolicy
    }
    case Policy_KindEnum.geofence: {
      const geofencePolicy: IPolicyGeofence = {
        ...basePolicy,
        kind: Policy_KindEnum.geofence,
        config: {},
        rules: fetchedPolicy.rules.map((rule) => ({
          ...getBaseRule(rule),
          rule_type: Policy_KindEnum.geofence,
          config: {},
        })),
      }
      return geofencePolicy
    }
    case Policy_KindEnum.parking: {
      const parkingPolicy: IPolicyParking = {
        ...basePolicy,
        kind: Policy_KindEnum.parking,
        config: {},
        rules: fetchedPolicy.rules.map((rule) => ({
          ...getBaseRule(rule),
          rule_type: Policy_KindEnum.parking,
          rule_units: PolicyRule_RuleUnitsEnum.minutes,
          authorized_parking_zones: rule.authorized_parking_zones ?? [],
          buffered_authorized_parking_zones: rule.buffered_authorized_parking_zones ?? [],
          buffered_prohibited_parking_zones: rule.buffered_prohibited_parking_zones ?? [],
          prohibited_parking_zones: rule.prohibited_parking_zones ?? [],
        })),
      }
      return parkingPolicy
    }
    case Policy_KindEnum.absolute_fleet_size: {
      const absoluteFleetSizePolicy: IPolicyAbsoluteFleetSize = {
        ...basePolicy,
        kind: Policy_KindEnum.absolute_fleet_size,
        config: {},
        rules: fetchedPolicy.rules.map((rule) => ({
          ...getBaseRule(rule),
          rule_type: Policy_KindEnum.absolute_fleet_size,
          minimum: rule.minimum as number,
          maximum: rule.maximum as number,
        })),
      }
      return absoluteFleetSizePolicy
    }
    case Policy_KindEnum.fleet_budget: {
      const fleetBudgetPolicy: IPolicyFleetBudget = {
        ...basePolicy,
        kind: Policy_KindEnum.fleet_budget,
        config: {},
        rules: fetchedPolicy.rules.map((rule) => ({
          ...getBaseRule(rule),
          rule_type: Policy_KindEnum.fleet_budget,
          maximum: rule.maximum as number,
          rate_amount: rule.rate_amount as number,
        })),
      }
      return fleetBudgetPolicy
    }
    case Policy_KindEnum.trip_taxation: {
      const tripTaxationPolicy: IPolicyTripTaxation = {
        ...basePolicy,
        config: {},
        kind: Policy_KindEnum.trip_taxation,
        rules: fetchedPolicy.rules.map((rule) => {
          return {
            ...getBaseRule(rule),
            rule_type: Policy_KindEnum.trip_taxation,
            rule_units: rule.rule_units as IPolicyRuleTripTaxation['rule_units'],
            rate_amount: Math.abs(rule.rate_amount ?? 0),
            rate_amount_type: (rule.rate_amount ?? 0) < 0 ? 'subsidy' : 'fee',
          }
        }),
        due_date_kind: (fetchedPolicy as unknown as IPolicyTripTaxation).due_date_kind,
        fee_calculation_method: (fetchedPolicy as unknown as IPolicyTripTaxation).fee_calculation_method,
      }
      return tripTaxationPolicy
    }
    case Policy_KindEnum.beginner_speed_limit: {
      const speedPolicy: IPolicyBeginnerSpeedLimit = {
        ...basePolicy,
        kind: Policy_KindEnum.beginner_speed_limit,
        config: {},
        minimum_time_duration: fetchedPolicy.minimum_time_duration ?? 0,
        rules: fetchedPolicy.rules.map((rule) => ({
          ...getBaseRule(rule),
          rule_units: rule.rule_units as IPolicyRuleBeginnerSpeedLimit['rule_units'],
          rule_type: Policy_KindEnum.beginner_speed_limit,
          maximum: rule.maximum,
        })),
      }
      return speedPolicy
    }
    case Policy_KindEnum.beginner_allowed_hours: {
      const speedPolicy: IPolicyBeginnerAllowedHours = {
        ...basePolicy,
        kind: Policy_KindEnum.beginner_allowed_hours,
        config: {},
        rules: fetchedPolicy.rules.map((rule) => ({
          ...getBaseRule(rule),
          rule_type: Policy_KindEnum.beginner_allowed_hours,
        })),
      }
      return speedPolicy
    }
    // Not implemented yet, for demo purpose only
    case Policy_KindEnum.time: {
      const timePolicy: IPolicyTime = {
        ...basePolicy,
        kind: Policy_KindEnum.time,
        config: {},
        rules: fetchedPolicy.rules.map((r) => ({
          ...getBaseRule(r),
          rule_type: Policy_KindEnum.time,
          rule_units: r.rule_units,
          minimum: r.minimum,
          maximum: r.maximum,
        })),
      }
      return timePolicy
    }
    // Not implemented yet, for demo purpose only
    case Policy_KindEnum.user: {
      const userPolicy: IPolicyUser = {
        ...basePolicy,
        kind: Policy_KindEnum.user,
        config: {},
        rules: fetchedPolicy.rules.map((r) => ({
          ...getBaseRule(r),
          rule_type: Policy_KindEnum.user,
          rule_units: r.rule_units,
          minimum: r.minimum,
          maximum: r.maximum,
        })),
      }
      return userPolicy
    }
    // Not implemented yet, for demo purpose only
    case Policy_KindEnum.abandoned: {
      const ruleAbandoned = fetchedPolicy.rules[0]
      const abandonedPolicy: IPolicyAbandoned = {
        ...basePolicy,
        kind: Policy_KindEnum.abandoned,
        config: {},
        rules: [
          {
            ...getBaseRule(ruleAbandoned),
            rule_type: Policy_KindEnum.abandoned,
          },
        ],
      }
      return abandonedPolicy
    }
    // Not implemented yet, for demo purpose only
    case Policy_KindEnum.sidewalk: {
      const ruleSidewalk = fetchedPolicy.rules[0]
      const sidewalkPolicy: IPolicySidewalk = {
        ...basePolicy,
        kind: Policy_KindEnum.sidewalk,
        config: {},
        rules: [
          {
            ...getBaseRule(ruleSidewalk),
            rule_type: Policy_KindEnum.sidewalk,
          },
        ],
      }
      return sidewalkPolicy
    }
    // Not implemented yet, for demo purpose only
    case Policy_KindEnum.balancing: {
      const ruleBalancing = fetchedPolicy.rules[0]
      const balancingPolicy: IPolicyBalancing = {
        ...basePolicy,
        kind: Policy_KindEnum.balancing,
        config: {
          minimum_fleet_percentage: fetchedPolicy.config.minimum_fleet_percentage || 0,
        },
        rules: [
          {
            ...getBaseRule(ruleBalancing),
            rule_type: Policy_KindEnum.balancing,
          },
        ],
      }
      return balancingPolicy
    }
    case Policy_KindEnum.vehicle_deployment: {
      const vehicleDeploymentPolicy: IPolicyVehicleDeployment = {
        ...basePolicy,
        kind: Policy_KindEnum.vehicle_deployment,
        config: {},
        rules: fetchedPolicy.rules.map((r) => ({
          ...getBaseRule(r),
          rule_type: Policy_KindEnum.vehicle_deployment,
          rate_amount: r.rate_amount ?? 0,
        })),
      }
      return vehicleDeploymentPolicy
    }
    case Policy_KindEnum.maintenance: {
      const maintenancePolicy: IPolicyMaintenance = {
        ...basePolicy,
        kind: Policy_KindEnum.maintenance,
        config: {},
        maintenance_kinds: fetchedPolicy.maintenance_kinds ?? [],
        rules: fetchedPolicy.rules.map((rule) => ({
          ...getBaseRule(rule),
          rule_type: Policy_KindEnum.maintenance,
          rule_units: rule.rule_units!,
          minimum: rule.minimum! / 60, // secondes to minutes
          maximum: rule.maximum!,
        })),
      }
      return maintenancePolicy
    }
    case Policy_KindEnum.dwell_time: {
      const dwellTimePolicy: IPolicyDwellTime = {
        ...basePolicy,
        kind: Policy_KindEnum.dwell_time,
        config: {},
        vehicle_events_and_states: fetchedPolicy.vehicle_events_and_states as Record<
          AgencySettings_VehicleStatesEnum,
          never[]
        >,
        confirmation_delay: basePolicy.confirmation_delay!, // confirmation_delay is required for dwell_time
        notification_delay: Temporal.Duration.from(
          fetchedPolicy.notification_delay ? fetchedPolicy.notification_delay : 'PT0H'
        ).total({ unit: 'hours' }),
        rules: fetchedPolicy.rules.map((rule) => ({
          ...getBaseRule(rule),
          rule_type: Policy_KindEnum.dwell_time,
          rule_units: PolicyRule_RuleUnitsEnum.minutes,
          authorized_parking_zones: rule.authorized_parking_zones ?? [],
          buffered_authorized_parking_zones: rule.buffered_authorized_parking_zones ?? [],
          buffered_prohibited_parking_zones: rule.buffered_prohibited_parking_zones ?? [],
          prohibited_parking_zones: rule.prohibited_parking_zones ?? [],
        })),
      }
      return dwellTimePolicy
    }
    default:
      return assertUnreachable(fetchedPolicy.kind)
  }
}

/**
 * Convert the common part of the rule specified in the front to the
 * format expected by the backend
 * @param rule
 */
const getBaseRuleToSave = (rule: PolicyRule): Omit<RuleInput, 'rule_type' | 'config'> => ({
  name: rule.name,
  rule_id: rule.rule_id,
  geographies: rule.geographies,
  buffered_geographies: rule.buffered_geographies,
  vehicle_types: rule.vehicle_types as unknown as RuleInput['vehicle_types'],
  timeslots: rule.timeslots as unknown as RuleInput['timeslots'],
  proration_kind: rule.proration_kind as unknown as RuleInput_ProrationKindEnum,
  proration_areas_mapping: rule.proration_areas_mapping,
  proration_time_spent_report_area: rule.proration_time_spent_report_area,
})

/**
 * Convert the frontend policy to the format expected by the backend
 * @param currentPolicy
 * @param publish
 */
const PENALTY_FREQUENCIES_TO_SAVE: Record<PenaltyFrequency, PolicyInput_PenaltyFrequencyStrEnum> = {
  [PenaltyFrequency.hourly]: PolicyInput_PenaltyFrequencyStrEnum.hourly,
  [PenaltyFrequency.daily]: PolicyInput_PenaltyFrequencyStrEnum.daily,
}

export const formatCurrentPolicyToSave = (currentPolicy: Policy, publish: boolean): PolicyInput => {
  const basePolicyToSave: Omit<PolicyInput, 'rules' | 'config'> = {
    // No Id because the api payload doesn't have one.
    name: currentPolicy.name,
    description: currentPolicy.description,
    private_description: currentPolicy.private_description,
    providers: currentPolicy.providers,
    start_date: zonedDateTimeToISOString(currentPolicy.start_date),
    end_date: currentPolicy.end_date ? zonedDateTimeToISOString(currentPolicy.end_date) : undefined,
    due_date_kind: currentPolicy.due_date_kind
      ? (currentPolicy.due_date_kind as unknown as PolicyInput_DueDateKindEnum)
      : undefined,
    recurrence_repeat_every: currentPolicy.recurrence_repeat_every
      ? Math.max(currentPolicy.recurrence_repeat_every, 0)
      : undefined,
    recurrence_ends_after: currentPolicy.recurrence_ends_after
      ? Math.max(currentPolicy.recurrence_ends_after, 0)
      : undefined,
    recurrence_kind: (currentPolicy.recurrence_kind as unknown as PolicyInput_RecurrenceKindEnum) ?? undefined,
    publish,
    prev_policies: currentPolicy.prev_policies,
    kind: currentPolicy.kind as string as PolicyInput_KindEnum,
    confirmation_delay_seconds: (currentPolicy.confirmation_delay ?? 0) * getPolicyDelayFactor(currentPolicy.kind),
    penalty_delay_seconds: (currentPolicy.penalty_delay ?? 0) * getPolicyDelayFactor(currentPolicy.kind),
    fixed_price: currentPolicy.fixed_price,
    flat_penalty: currentPolicy.flat_penalty,
    minimum_time_duration: currentPolicy.minimum_time_duration,
    penalty_amount: currentPolicy.penalty_amount,
    penalty_frequency_str: currentPolicy.penalty_frequency
      ? PENALTY_FREQUENCIES_TO_SAVE[currentPolicy.penalty_frequency]
      : undefined,
    vehicle_events_and_states: currentPolicy.vehicle_events_and_states ?? {},
    smart_patrol: currentPolicy.kind === Policy_KindEnum.parking ? currentPolicy.smart_patrol : undefined,
    is_mds: currentPolicy.is_mds,
  }

  switch (currentPolicy.kind) {
    case Policy_KindEnum.speed: {
      return {
        ...basePolicyToSave,
        config: {},
        rules: currentPolicy.rules.map((rule) => ({
          ...getBaseRuleToSave(rule),
          maximum: rule.maximum ?? 0,
          buffer_speed: rule.buffer_speed || undefined,
          rule_type: RuleInput_RuleTypeEnum.speed,
          rule_units: rule.rule_units as unknown as RuleInput['rule_units'],
        })),
      }
    }
    case Policy_KindEnum.beginner_speed_limit: {
      return {
        ...basePolicyToSave,
        config: {},
        rules: currentPolicy.rules.map((rule) => ({
          ...getBaseRuleToSave(rule),
          maximum: rule.maximum ?? 0,
          rule_type: RuleInput_RuleTypeEnum.beginner_speed_limit,
          rule_units: rule.rule_units as unknown as RuleInput['rule_units'],
        })),
      }
    }
    case Policy_KindEnum.beginner_allowed_hours: {
      return {
        ...basePolicyToSave,
        config: {},
        rules: currentPolicy.rules.map((rule) => ({
          ...getBaseRuleToSave(rule),
          rule_type: RuleInput_RuleTypeEnum.beginner_allowed_hours,
        })),
      }
    }
    case Policy_KindEnum.absolute_fleet_size: {
      return {
        ...basePolicyToSave,
        config: {},
        rules: currentPolicy.rules.map((rule) => ({
          ...getBaseRuleToSave(rule),
          minimum: rule.minimum,
          maximum: rule.maximum,
          rule_type: RuleInput_RuleTypeEnum.absolute_fleet_size,
        })),
      }
    }
    case Policy_KindEnum.permit: {
      return {
        ...basePolicyToSave,
        config: {},
        rules: currentPolicy.rules.map((rule) => ({
          ...getBaseRuleToSave(rule),
          minimum: rule.minimum,
          maximum: rule.maximum,
          rule_type: RuleInput_RuleTypeEnum.permit,
          rate_amount: rule.rate_amount,
        })),
      }
    }
    case Policy_KindEnum.geofence: {
      return {
        ...basePolicyToSave,
        config: {},
        rules: currentPolicy.rules.map((rule) => ({
          ...getBaseRuleToSave(rule),
          rule_type: RuleInput_RuleTypeEnum.geofence,
        })),
      }
    }
    case Policy_KindEnum.parking: {
      return {
        ...basePolicyToSave,
        config: {},
        rules: currentPolicy.rules.map((rule) => ({
          ...getBaseRuleToSave(rule),
          rule_type: RuleInput_RuleTypeEnum.parking,
          rule_units: PolicyRule_RuleUnitsEnum.minutes as unknown as RuleInput['rule_units'],
          authorized_parking_zones: rule.authorized_parking_zones,
          prohibited_parking_zones: rule.prohibited_parking_zones,
          buffered_authorized_parking_zones: rule.buffered_authorized_parking_zones,
          buffered_prohibited_parking_zones: rule.buffered_prohibited_parking_zones,
        })),
      }
    }
    case Policy_KindEnum.time: {
      return {
        ...basePolicyToSave,
        config: {},
        rules: currentPolicy.rules.map((rule) => ({
          ...getBaseRuleToSave(rule),
          rule_type: RuleInput_RuleTypeEnum.time,
          rule_units: rule.rule_units as unknown as RuleInput['rule_units'],
          minimum: rule.minimum,
          maximum: rule.maximum,
        })),
      }
    }
    case Policy_KindEnum.user: {
      return {
        ...basePolicyToSave,
        config: {},
        rules: currentPolicy.rules.map((rule) => ({
          ...getBaseRuleToSave(rule),
          rule_type: RuleInput_RuleTypeEnum.user,
          minimum: rule.minimum,
          maximum: rule.maximum,
        })),
      }
    }
    case Policy_KindEnum.abandoned: {
      return {
        ...basePolicyToSave,
        config: {},
        rules: currentPolicy.rules.map((rule) => ({
          ...getBaseRuleToSave(rule),
          rule_type: RuleInput_RuleTypeEnum.abandoned,
        })),
      }
    }
    case Policy_KindEnum.trip_taxation: {
      return {
        ...basePolicyToSave,
        config: {},
        rules: currentPolicy.rules.map((rule) => ({
          ...getBaseRuleToSave(rule),
          rule_type: RuleInput_RuleTypeEnum.trip_taxation,
          rule_units: rule.rule_units as unknown as RuleInput['rule_units'],
          rate_amount: rule.rate_amount_type === 'fee' ? rule.rate_amount : -rule.rate_amount,
        })),
        fee_calculation_method: currentPolicy.fee_calculation_method as unknown as PolicyInput_FeeCalculationMethodEnum,
      }
    }
    case Policy_KindEnum.sidewalk: {
      return {
        ...basePolicyToSave,
        config: {},
        rules: currentPolicy.rules.map((rule) => ({
          ...getBaseRuleToSave(rule),
          rule_type: RuleInput_RuleTypeEnum.sidewalk,
        })),
      }
    }
    case Policy_KindEnum.balancing: {
      return {
        ...basePolicyToSave,
        config: {
          minimum_fleet_percentage: currentPolicy.config.minimum_fleet_percentage,
        },
        rules: currentPolicy.rules.map((rule) => ({
          ...getBaseRuleToSave(rule),
          rule_type: RuleInput_RuleTypeEnum.balancing,
        })),
      }
    }
    case Policy_KindEnum.vehicle_deployment: {
      return {
        ...basePolicyToSave,
        rules: currentPolicy.rules.map((rule) => ({
          ...getBaseRuleToSave(rule),
          rule_type: RuleInput_RuleTypeEnum.vehicle_deployment,
          rate_amount: rule.rate_amount,
          rule_units: RuleInput_RuleUnitsEnum.amount,
        })),
      }
    }
    case Policy_KindEnum.maintenance: {
      const maintenance_kinds: PolicyInput_MaintenanceKindsEnum[] = currentPolicy.maintenance_kinds.map(
        (maintenance_kind) => {
          if (maintenance_kind === Policy_MaintenanceKindsEnum.on_street) {
            return PolicyInput_MaintenanceKindsEnum.on_street
          }
          return PolicyInput_MaintenanceKindsEnum.warehouse
        }
      )
      return {
        ...basePolicyToSave,
        maintenance_kinds: maintenance_kinds,
        rules: currentPolicy.rules.map((rule) => ({
          ...getBaseRuleToSave(rule),
          rule_type: RuleInput_RuleTypeEnum.maintenance,
          rule_units: rule.rule_units as unknown as RuleInput_RuleUnitsEnum,
          minimum: rule.minimum * 60, // convert the amount in minutes to seconds
          maximum: rule.maximum,
        })),
      }
    }
    case Policy_KindEnum.fleet_budget: {
      return {
        ...basePolicyToSave,
        rules: currentPolicy.rules.map((rule) => ({
          ...getBaseRuleToSave(rule),
          rule_type: RuleInput_RuleTypeEnum.fleet_budget,
          rule_units: RuleInput_RuleUnitsEnum.days,
          maximum: rule.maximum,
          rate_amount: rule.rate_amount,
        })),
      }
    }
    case Policy_KindEnum.dwell_time: {
      return {
        ...basePolicyToSave,
        config: {},
        notification_delay: Temporal.Duration.from({ seconds: Math.round(currentPolicy.notification_delay * 3600) })
          .round({ largestUnit: 'hours' })
          .toString(),
        rules: currentPolicy.rules.map((rule) => ({
          ...getBaseRuleToSave(rule),
          rule_type: RuleInput_RuleTypeEnum.dwell_time,
          rule_units: RuleInput_RuleUnitsEnum.hours,
          authorized_parking_zones: rule.authorized_parking_zones,
          prohibited_parking_zones: rule.prohibited_parking_zones,
          buffered_authorized_parking_zones: rule.buffered_authorized_parking_zones,
          buffered_prohibited_parking_zones: rule.buffered_prohibited_parking_zones,
        })),
      }
    }
    default:
      return assertUnreachable(currentPolicy)
  }
}

const TIMESLOT_POLICY_KINDS = [
  Policy_KindEnum.absolute_fleet_size,
  Policy_KindEnum.geofence,
  Policy_KindEnum.parking,
  Policy_KindEnum.speed,
  Policy_KindEnum.fleet_budget,
]

export function isTimeSlotAvailable(currentPolicy: Policy): boolean {
  return TIMESLOT_POLICY_KINDS.includes(currentPolicy.kind)
}

export function getRule(ruleId: string, rules: PolicyRule[]): PolicyRule {
  const rule = rules.find((rule: any) => rule.rule_id == ruleId)
  if (rule) {
    return rule
  }
  return rules[0]
}

export function isEndTimeBeforeStartTime(timeslot: PolicyTimeslot): boolean {
  if (
    !timeslot.start_time ||
    !timeslot.end_time ||
    // The timeslot input returns 'Invalid Date' if a user is still typing and the value is incomplete (e.g. "10:" instead of "10:30")
    timeslot.start_time === 'Invalid Date' ||
    timeslot.end_time === 'Invalid Date'
  ) {
    return false
  }

  const [startTimeHour = '0', startTimeMinute = '0', startTimeSecond = '0'] = timeslot.start_time.split(':')
  const [endTimeHour = '0', endTimeMinute = '0', endTimeSecond = '0'] = timeslot.end_time.split(':')
  const startTime = Temporal.PlainTime.from({
    hour: parseInt(startTimeHour),
    minute: parseInt(startTimeMinute),
    second: parseInt(startTimeSecond),
  })
  const endTime = Temporal.PlainTime.from({
    hour: parseInt(endTimeHour),
    minute: parseInt(endTimeMinute),
    second: parseInt(endTimeSecond),
  })
  return Temporal.PlainTime.compare(startTime, endTime) > 0
}

export function areDictEquals(dict1: any, dict2: any): boolean {
  // Simple equality to test for null/undefined
  if (dict1 == null || dict2 == null) {
    return dict1 === dict2
  }

  // Test if unsorted keys are equals
  const dict2Keys = Object.keys(dict2).sort()
  if (
    Object.keys(dict1)
      .sort()
      .some((value, index) => value !== dict2Keys[index])
  ) {
    return false
  }

  for (const key of Object.keys(dict1)) {
    // Specifically test for nested Array
    // Could be a recursive call to test for an infinite amount of recursive objects
    if (
      dict1[key] instanceof Array &&
      (dict1[key].length !== dict2[key]?.length ||
        dict1[key].sort().some((value: any, index: number) => value !== dict2[key].sort?.()[index]))
    ) {
      return false
    }
  }

  return true
}
